import type { Message } from "@/lib/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { MessageBubble } from "./MessageBubble";

type MessageListProps = {
  messages: Message[];
  isStreaming: boolean;
  onStop: () => void;
  onReload: () => void;
  activeId: string | null;
  onOpenArtifact?: (id: string) => void;
  inlineArtifactExpanded?: boolean;
  onToggleInlineArtifact?: () => void;
  footer?: React.ReactNode;
};

const ARTIFACT_TOOLS = new Set([
  "artifact_create",
  "artifact_write",
  "artifact_edit",
  "artifact_read",
]);

const SCROLL_THRESHOLD = 40;
const FOLLOW_EASE = 0.18;
const FOLLOW_MIN_STEP = 1.5;

export function MessageList({ messages, isStreaming, onStop, onReload, activeId, onOpenArtifact, inlineArtifactExpanded, onToggleInlineArtifact, footer }: MessageListProps) {
  const [isAtBottom, setIsAtBottom] = useState(true);
  const userScrolledUp = useRef(false);
  const isAutoScrolling = useRef(false);
  const isTouching = useRef(false);
  const rafRef = useRef<number | null>(null);

  const stopFollow = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    isAutoScrolling.current = false;
  }, []);

  const followBottom = useCallback(() => {
    if (rafRef.current != null) return;
    const tick = () => {
      const el = document.documentElement;
      if (isTouching.current || userScrolledUp.current) {
        rafRef.current = null;
        isAutoScrolling.current = false;
        return;
      }
      const target = el.scrollHeight - el.clientHeight;
      const distance = target - el.scrollTop;
      if (distance <= 0.5) {
        rafRef.current = null;
        isAutoScrolling.current = false;
        setIsAtBottom(true);
        return;
      }
      const step = Math.max(FOLLOW_MIN_STEP, distance * FOLLOW_EASE);
      isAutoScrolling.current = true;
      el.scrollTop = Math.min(target, el.scrollTop + step);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const jumpToBottom = useCallback(() => {
    if (isTouching.current) return;
    stopFollow();
    const el = document.documentElement;
    isAutoScrolling.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    userScrolledUp.current = false;
    setIsAtBottom(true);
  }, [stopFollow]);

  const prevActiveId = useRef<string | null>(activeId);
  useEffect(() => {
    const prev = prevActiveId.current;
    prevActiveId.current = activeId;
    if (prev === null && activeId !== null && userScrolledUp.current) {
      return;
    }
    const el = document.documentElement;
    userScrolledUp.current = false;
    stopFollow();
    el.scrollTop = el.scrollHeight;
    setIsAtBottom(true);
  }, [activeId, stopFollow]);

  useEffect(() => {
    const handleScroll = () => {
      const el = document.documentElement;
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD;
      setIsAtBottom(atBottom);
      if (isAutoScrolling.current) {
        if (atBottom) isAutoScrolling.current = false;
        return;
      }
      if (!atBottom) {
        userScrolledUp.current = true;
        stopFollow();
      } else {
        userScrolledUp.current = false;
      }
    };
    const onTouchStart = () => {
      isTouching.current = true;
      stopFollow();
    };
    const onTouchEnd = () => {
      isTouching.current = false;
      const el = document.documentElement;
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD;
      if (!atBottom) userScrolledUp.current = true;
      else if (isStreaming) followBottom();
    };
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        userScrolledUp.current = true;
        stopFollow();
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("wheel", onWheel, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("wheel", onWheel);
    };
  }, [followBottom, stopFollow, isStreaming]);

  const prevUserCount = useRef(0);
  useEffect(() => {
    const userCount = messages.reduce((n, m) => (m.role === "user" ? n + 1 : n), 0);
    const newUserMessage = userCount > prevUserCount.current;
    prevUserCount.current = userCount;
    if (newUserMessage) {
      userScrolledUp.current = false;
      followBottom();
      return;
    }
    if (userScrolledUp.current) return;
    if (!isStreaming) return;
    const el = document.documentElement;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance <= SCROLL_THRESHOLD) followBottom();
  }, [messages, isStreaming, followBottom]);

  useEffect(() => () => stopFollow(), [stopFollow]);

  const lastUserIndex = messages.findLastIndex((m) => m.role === "user");

  let mostRecentArtifactKey: string | undefined;
  for (let i = messages.length - 1; i >= 0 && !mostRecentArtifactKey; i--) {
    const tcs = messages[i].toolCalls ?? [];
    for (let j = tcs.length - 1; j >= 0; j--) {
      if (ARTIFACT_TOOLS.has(tcs[j].tool)) {
        mostRecentArtifactKey = `${messages[i].id}:${j}`;
        break;
      }
    }
  }

  return (
    <div
      className="relative flex-1 pb-30"
      style={{ paddingTop: "calc(5rem + var(--titlebar-h, 0px))" }}
    >
      <div
        className="flex flex-col gap-3 px-3 py-4 sm:px-4"
      >
        <div className="mx-auto w-full max-w-4xl space-y-4">
          {messages.map((msg, i) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              isStreaming={isStreaming}
              isLastMessage={i === messages.length - 1}
              isLastUser={i === lastUserIndex}
              onStop={onStop}
              onReload={onReload}
              onOpenArtifact={onOpenArtifact}
              mostRecentArtifactKey={mostRecentArtifactKey}
              inlineArtifactExpanded={inlineArtifactExpanded}
              onToggleInlineArtifact={onToggleInlineArtifact}
            />
          ))}
          {footer}
        </div>
      </div>

      <button
        onClick={jumpToBottom}
        aria-label="Scroll to bottom"
        className={"fixed bottom-30 right-4 z-40 flex size-9 items-center justify-center rounded-full border bg-background shadow-md transition-colors hover:bg-muted " + (isAtBottom ? "hidden" : "")}
      >
        <i className="fa-solid fa-chevron-down text-sm" />
      </button>
    </div>
  );
}
